import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { defer, of, Subscription } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store, ActionReducerMap, MetaReducer, createSelector } from '@ngrx/store';
import { routerReducer } from '@ngrx/router-store';
import { get } from 'lodash';
import { SentryService, CommonService, WebSocketService } from '@core/utils';
import { ISentryUserIdentity } from '@shared/models';
import { APP_CONSTANT } from '@constants';
import { TranslocoService } from '@jsverse/transloco';
import { IUserAccount } from './user.model';
import { AuthService } from '@auth-service';


// Auth Actions Enum
export enum AuthActionTypes {
    Login = '[Login] Action',
    Logout = '[Logout] Action',
    UserRequested = '[Request User] Action',
    UserLoaded = '[Load User] Auth API',
    ErrorOccured = '[Login] Auth API'
}

// Auth Actions
export class Login {
    readonly type = AuthActionTypes.Login;
    constructor(public payload: { user: IUserAccount, authToken: string }) { }
}

export class Logout {
    readonly type = AuthActionTypes.Logout;
    constructor(public payload?: { returnUrl?: string }) { }
}

export class UserRequested {
    readonly type = AuthActionTypes.UserRequested;
}

export class UserLoaded {
    readonly type = AuthActionTypes.UserLoaded;
    constructor(public payload: { user: IUserAccount }) { }
}

export class ErrorOccured {
    readonly type = AuthActionTypes.ErrorOccured;
    constructor(public payload: { error: any }) { }
}

export type AuthActions = Login | Logout | UserRequested | UserLoaded | ErrorOccured;

// Auth State
export interface AuthState {
    loggedIn: boolean;
    authToken: string | undefined;
    user: IUserAccount | undefined;
    isUserLoaded: boolean;
}

export const initialAuthState: AuthState = {
    loggedIn: false,
    authToken: undefined,
    user: undefined,
    isUserLoaded: false
};

// Auth Reducer
export function authReducer(state = initialAuthState, action: AuthActions): AuthState {
    switch (action.type) {
        case AuthActionTypes.Login:
            return { ...state, loggedIn: true, authToken: action.payload.authToken, isUserLoaded: false };
        case AuthActionTypes.Logout:
            return initialAuthState;
        case AuthActionTypes.UserLoaded:
            return { ...state, user: action.payload.user, isUserLoaded: true, loggedIn: true };
        default:
            return state;
    }
}

// Selectors
export const selectAuthState = (state: any) => state.auth;
export const isLoggedIn = createSelector(selectAuthState, auth => Boolean(auth.loggedIn));
export const isLoggedOut = createSelector(isLoggedIn, loggedIn => !loggedIn);
export const currentAuthToken = createSelector(selectAuthState, auth => auth.authToken);
export const isUserLoaded = createSelector(selectAuthState, auth => auth.isUserLoaded);
export const currentUser = createSelector(selectAuthState, auth => auth.user);
export const userEnvironment = createSelector(selectAuthState, auth => auth.user?.userenvironment);

// App State
export interface AppState { }
export const reducers: ActionReducerMap<AppState> = { router: routerReducer };
export const metaReducers: MetaReducer<AppState>[] = [];

// Auth Effects
@Injectable({ providedIn: 'root' })
export class AuthEffects implements OnDestroy {

    // Inject dependencies
    private actions$ = inject(Actions);
    private router = inject(Router);
    private auth = inject(AuthService);
    private cs = inject(CommonService);
    readonly sentryService = inject(SentryService);
    private store = inject<Store<AppState>>(Store);
    private readonly translocoService = inject(TranslocoService);
    private readonly wsService = inject(WebSocketService);


    login$ = createEffect(() => this.actions$.pipe(
        ofType<Login>(AuthActionTypes.Login),
        tap((action: any) => {
            this.cs.setIntoLocalStorage(APP_CONSTANT.JWT_TOKEN_KEY, action.payload.authToken);
            this.cs.setIntoLocalStorage(APP_CONSTANT.LOGGED_USER, action.payload.user);
            const params = new URLSearchParams(window.location.search);
            const queryLangParam = params.get('lang');
            const localeId = null === queryLangParam ? this.cs.getUserLocale(get(action.payload, 'user.environment.language', '840')).locale : queryLangParam;
            this.cs.setIntoLocalStorage(APP_CONSTANT.USER_LOCALE_KEY, localeId);
            this.translocoService.setActiveLang(localeId);
            this.store.dispatch(new UserRequested());
        })
    ), { dispatch: false });


    logout$ = createEffect(() => this.actions$.pipe(
        ofType<Logout>(AuthActionTypes.Logout),
        tap((action: any) => {
            this.cs.clearAll();
            this.auth.flushRoleAndPermissions();
            this.sentryService.resetAll();
            this.wsService.close();
            const returnUrl = action.payload?.returnUrl || this.returnUrl;
            this.router.navigate(['/auth/login'], {
                queryParams: { returnUrl },
            });
        })
    ), { dispatch: false });


    loadUser$ = createEffect(() => this.actions$.pipe(
        ofType<UserRequested>(AuthActionTypes.UserRequested),
        withLatestFrom(this.store.pipe(select(isUserLoaded))),
        filter(([action, checkUserLoaded]) => !checkUserLoaded),
        mergeMap(([action, checkUserLoaded]) => this.auth.getUserDetails()),
        tap((result) => {
            if (result) {
                // Save Sentry User
                const sentryUserIdentity: ISentryUserIdentity = {
                    id: get(result, 'user.id'),
                    username: get(result, 'user.name'),
                    email: get(result, 'user.email'),
                    companyname: get(result, 'company.name', '')
                };
                this.sentryService.setUser(sentryUserIdentity);
                this.store.dispatch(new UserLoaded({ user: result }));
            } else {
                this.store.dispatch(new Logout());
            }
        })
    ), { dispatch: false });


    init$ = createEffect(() => defer(() => {
        const authToken = this.cs.getFromLocalStorage(APP_CONSTANT.JWT_TOKEN_KEY);
        const user = this.cs.getFromLocalStorage(APP_CONSTANT.LOGGED_USER);
        let observableResult = of({ type: 'NO_ACTION' });
        if (authToken) {
            observableResult = of(new Login({ user, authToken }));
        }
        return observableResult;
    }));

    // Private
    private returnUrl: string | null;
    private unsubscribe: Subscription[] = [];

    /**
     * Creates an instance of AuthEffects.
     * @param {Actions} actions$
     * @param {Router} router
     * @param {AuthService} auth
     * @param {CommonService} cs
     * @param {SentryService} sentryService
     * @param {Store<AppState>} store
     * @param {TranslocoService} translocoService
     * @param {WebSocketService} wsService
     * @memberof AuthEffects
     */
    constructor() {
        const routerSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.returnUrl = event.urlAfterRedirects === APP_CONSTANT.ERROR_PATH ? null : event.url;
            }
        });
        this.unsubscribe.push(routerSubscription);
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
    }

}

